// CUSTOMIZATION:
// - used registry container instead of semantic-ui + styles
// - changed root from icon to string
// - changed divider
// - added dropdown menu for breadcrumbs for mobile

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { Breadcrumb, Segment } from 'semantic-ui-react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useMediaQuery } from 'usehooks-ts';

import config from '@plone/registry';
import { getBreadcrumbs } from '@plone/volto/actions/breadcrumbs/breadcrumbs';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { hasApiExpander } from '@plone/volto/helpers/Utils/Utils';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import homeSVG from '@plone/volto/icons/home.svg';
import { Select, SelectItem } from '@plone/components';

import '@redturtle/qubicaamf-theme/theme/custom/breadcrumbs.css';
import styles from '@redturtle/qubicaamf-theme/components/Container/container.module.css';

const messages = defineMessages({
  home: {
    id: 'Home',
    defaultMessage: 'Home',
  },
  breadcrumbs: {
    id: 'Breadcrumbs',
    defaultMessage: 'Breadcrumbs',
  },
  controlpanel: {
    id: 'Site Setup',
    defaultMessage: 'Site Setup',
  },
  a11y_breadcrumbs_menu: {
    id: 'a11y_breadcrumbs_menu',
    defaultMessage: 'Collapsed breadcrumbs.',
  },
});

// customization
const MobileBreadcrumb = ({ items }) => {
  const intl = useIntl();

  const menuItems = items.slice(0, -2);
  const breadcrumbItems = items.slice(-2, items.length);

  return [
    <Breadcrumb.Divider
      key="divider-start"
      content=">"
      className="breadcrumb-divider"
    />,
    <Select
      key="breadcrumbs-menu-select"
      className="breadcrumbs-menu"
      placeholder="..."
      aria-label={intl.formatMessage(messages.a11y_breadcrumbs_menu)}
    >
      {menuItems.map((item, index) => {
        return (
          <SelectItem
            key={item.url}
            className="breadcrumbs-menu-item"
            href={item.url}
            active={index === menuItems.length - 1}
          >
            {item.title}
          </SelectItem>
        );
      })}
    </Select>,

    breadcrumbItems.map((bc, index) => (
      <React.Fragment key={bc.url}>
        <Breadcrumb.Divider content=">" className="breadcrumb-divider" />
        {index < breadcrumbItems.length - 1 ? (
          <Link to={bc.url} className="section breadcrumb-link">
            {bc.title}
          </Link>
        ) : (
          <Breadcrumb.Section
            active
            className="section current-page-breadcrumb"
          >
            {bc.title}
          </Breadcrumb.Section>
        )}
      </React.Fragment>
    )),
  ];
};

const BreadcrumbsComponent = ({ pathname }) => {
  const isDesktop = useMediaQuery('(min-width: 768px');
  const intl = useIntl();
  const dispatch = useDispatch();
  const { pathname: realPath } = useLocation();
  const controlpanelItems = [
    {
      url: '/controlpanel',
      title: intl.formatMessage(messages.controlpanel),
    },
  ];

  // customization
  const Container = config.getComponent('Container').component || 'div';

  const items = useSelector(
    (state) =>
      realPath.startsWith('/controlpanel')
        ? controlpanelItems
        : state.breadcrumbs.items,
    shallowEqual,
  );
  const root = useSelector((state) => state.breadcrumbs.root);

  useEffect(() => {
    if (!hasApiExpander('breadcrumbs', getBaseUrl(pathname))) {
      dispatch(getBreadcrumbs(getBaseUrl(pathname)));
    }
  }, [dispatch, pathname]);

  return (
    <Segment
      role="navigation"
      aria-label={intl.formatMessage(messages.breadcrumbs)}
      className="breadcrumbs"
      secondary
      vertical
    >
      <Container className={styles.container}>
        <Breadcrumb>
          <Link
            to={root || '/'}
            className="section"
            title={intl.formatMessage(messages.home)}
          >
            {/* customization */}
            {intl.formatMessage(messages.home)}
          </Link>
          {/* customization */}
          {isDesktop || items.length < 3 ? (
            items.map((item, index, items) => {
              return [
                <Breadcrumb.Divider
                  key={`divider-${item.url}`}
                  content=">"
                  className="breadcrumb-divider"
                />,
                index < items.length - 1 ? (
                  <Link key={item.url} to={item.url} className="section">
                    {item.title}
                  </Link>
                ) : (
                  <Breadcrumb.Section key={item.url} active>
                    {item.title}
                  </Breadcrumb.Section>
                ),
              ];
            })
          ) : (
            <MobileBreadcrumb items={items} />
          )}
        </Breadcrumb>
      </Container>
    </Segment>
  );
};

BreadcrumbsComponent.propTypes = {
  pathname: PropTypes.string.isRequired,
};

export default BreadcrumbsComponent;
